import { get } from '@/lib/api';
import {
	EVIDENCE_TIER_LABELS,
	INCIDENT_STATUS_COLORS,
	INCIDENT_STATUS_LABELS,
	formatRelativeTime,
} from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { useQuery } from '@tanstack/react-query';
import type { IncidentSummary } from '@uncain/shared';
import { Link } from 'react-router';

export function IncidentsPage() {
	const { data, isLoading } = useQuery<{
		incidents: IncidentSummary[];
		meta: { total: number; limit: number; offset: number; hasMore: boolean };
	}>({
		queryKey: queryKeys.incidents.list(),
		queryFn: () =>
			get<{
				incidents: IncidentSummary[];
				meta: { total: number; limit: number; offset: number; hasMore: boolean };
			}>('/incidents'),
		refetchInterval: 30 * 1000,
	});

	if (isLoading) {
		return <div className="text-muted-foreground">Loading incidents...</div>;
	}

	const incidents = data?.incidents ?? [];

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">Incidents</h1>
				<span className="text-sm text-muted-foreground">
					{incidents.length} incident{incidents.length !== 1 ? 's' : ''}
				</span>
			</div>

			{incidents.length === 0 ? (
				<div className="rounded-lg border border-border bg-card p-8 text-center">
					<p className="text-muted-foreground">No incidents found.</p>
				</div>
			) : (
				<div className="divide-y divide-border rounded-lg border border-border bg-card">
					{incidents.map((incident) => (
						<Link
							key={incident.id}
							to={`/incidents/${incident.id}`}
							className="flex items-center justify-between p-4 transition-colors hover:bg-accent"
						>
							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<span
										className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
											INCIDENT_STATUS_COLORS[incident.status] ?? 'bg-gray-100 text-gray-800'
										}`}
									>
										{INCIDENT_STATUS_LABELS[incident.status] ?? incident.status}
									</span>
									<span className="text-xs text-muted-foreground">
										{EVIDENCE_TIER_LABELS[incident.evidenceTier] ?? incident.evidenceTier}
									</span>
								</div>
								<p className="text-sm font-medium text-foreground">
									{incident.summary || `Incident on ${incident.upstreamRepo}`}
								</p>
								<p className="text-xs text-muted-foreground">
									{incident.upstreamRepo} &middot; {incident.eventType} &middot;{' '}
									{incident.affectedCustomerCount} affected
								</p>
							</div>
							<span className="text-xs text-muted-foreground">
								{formatRelativeTime(incident.createdAt)}
							</span>
						</Link>
					))}
				</div>
			)}
		</div>
	);
}
